import {
  businessUnitInputSchema,
  businessUnitListSchema,
  businessUnitSchema,
  DEPARTMENT_TYPE_LABELS,
  departmentListSchema,
  departmentSchema,
  organizationDetailSchema,
  type BusinessUnit,
  type BusinessUnitInput,
  type Department,
  type DepartmentInput,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Network,
  Pencil,
  Plus,
  Search,
  Store,
  Users,
} from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { DepartmentForm } from '@/features/areas/DepartmentForm';
import { CompanyNav } from '@/features/organizations/CompanyNav';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

/** Administração › Áreas e unidades de uma empresa cliente (T-026b, RF-08). */
export function AreasPage() {
  const { organizationId = '' } = useParams();
  const { client } = useAuth();
  const org = useQuery({
    queryKey: ['organizations', 'detail', organizationId],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}`, { schema: organizationDetailSchema, signal }),
  });

  return (
    <PageContainer className="max-w-5xl">
      <Link
        to={`/empresas/${organizationId}`}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {org.data?.name ?? 'Empresa'}
      </Link>
      <PageHeader
        className="mt-4"
        title="Áreas e unidades"
        description="A estrutura da empresa: diretorias, gerências e departamentos, e as unidades de negócio. Pessoas e itens do BSC são ligados a elas."
      />
      <CompanyNav organizationId={organizationId} />
      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <DepartmentsSection organizationId={organizationId} />
        <UnitsSection organizationId={organizationId} />
      </div>
    </PageContainer>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section aria-label={title} className="rounded-lg border border-border bg-surface p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function DepartmentsSection({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<{ department?: Department; parentId?: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState<Department | null>(null);
  const key = ['departments', organizationId, showArchived];

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/departments`, {
        query: { archived: showArchived ? 'true' : undefined },
        schema: departmentListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['departments', organizationId] });

  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (d: Department) =>
      client.request(
        `/organizations/${organizationId}/departments/${d.id}/${d.archivedAt ? 'restore' : 'archive'}`,
        {
          method: 'POST',
          schema: departmentSchema,
        },
      ),
    onSuccess: async (d) => {
      setArchiving(null);
      toast.success(d.archivedAt ? `Área ${d.name} arquivada` : `Área ${d.name} restaurada`);
      await refresh();
    },
    onError: (err) => {
      setArchiving(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });

  async function save(input: DepartmentInput) {
    const target = editing?.department;
    const saved = target
      ? await client.request(`/organizations/${organizationId}/departments/${target.id}`, {
          method: 'PATCH',
          body: input,
          schema: departmentSchema,
        })
      : await client.request(`/organizations/${organizationId}/departments`, {
          method: 'POST',
          body: input,
          schema: departmentSchema,
        });
    setEditing(null);
    toast.success(target ? 'Área salva' : `Área ${saved.name} criada`);
    await refresh();
  }

  const items = query.data?.items ?? [];
  const roots = items.filter((d) => !d.parentId || !items.some((p) => p.id === d.parentId));
  const childrenOf = (id: string) => items.filter((d) => d.parentId === id);

  function renderNode(dep: Department, level: number): React.ReactNode {
    const children = childrenOf(dep.id);
    return (
      <li key={dep.id}>
        <div
          className={cn(
            'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md py-2 pr-1',
            dep.archivedAt && 'opacity-70',
          )}
          style={{ paddingLeft: `${level * 1.25}rem` }}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{dep.name}</span>
              <Badge variant="outline">{DEPARTMENT_TYPE_LABELS[dep.type]}</Badge>
              {dep.archivedAt ? <Badge variant="neutral">Arquivada</Badge> : null}
            </div>
            <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="size-3.5" aria-hidden />
              {dep.memberCount === 1 ? '1 pessoa' : `${dep.memberCount} pessoas`}
            </p>
          </div>
          <div className="flex gap-1">
            {!dep.archivedAt && dep.type !== 'DEPARTMENT' && can('administracao', 'create') ? (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Nova área abaixo de ${dep.name}`}
                onClick={() => setEditing({ parentId: dep.id })}
              >
                <Plus aria-hidden />
              </Button>
            ) : null}
            {!dep.archivedAt && can('administracao', 'edit') ? (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Editar ${dep.name}`}
                onClick={() => setEditing({ department: dep })}
              >
                <Pencil aria-hidden />
              </Button>
            ) : null}
            {can('administracao', 'delete') ? (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`${dep.archivedAt ? 'Restaurar' : 'Arquivar'} ${dep.name}`}
                disabled={toggleArchive.isPending}
                onClick={() => (dep.archivedAt ? toggleArchive.mutate(dep) : setArchiving(dep))}
              >
                {dep.archivedAt ? <ArchiveRestore aria-hidden /> : <Archive aria-hidden />}
              </Button>
            ) : null}
          </div>
        </div>
        {children.length ? (
          <ul className="border-l border-border" style={{ marginLeft: `${level * 1.25 + 0.5}rem` }}>
            {children.map((c) => renderNode(c, 1))}
          </ul>
        ) : null}
      </li>
    );
  }

  return (
    <Section
      title="Áreas"
      action={
        can('administracao', 'create') ? (
          <Button size="sm" onClick={() => setEditing({})}>
            <Plus aria-hidden />
            Nova área
          </Button>
        ) : null
      }
    >
      <label className="mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          className="size-4 accent-(--brand)"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        Mostrar arquivadas
      </label>
      {query.isPending ? (
        <LoadingState rows={4} label="Carregando áreas" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : items.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={Network}
          title="Nenhuma área cadastrada"
          description="Monte a estrutura da empresa a partir das diretorias; depois, gerências e departamentos abaixo delas."
        />
      ) : (
        <ul aria-label="Estrutura de áreas" aria-busy={query.isFetching}>
          {roots.map((r) => renderNode(r, 0))}
        </ul>
      )}

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing?.department ? 'Editar área' : 'Nova área'}
      >
        {editing !== null ? (
          <DepartmentForm
            departments={items}
            initial={editing.department}
            defaultParentId={editing.parentId}
            submitLabel={editing.department ? 'Salvar área' : 'Criar área'}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Arquivar a área?"
        description={`${archiving?.name ?? ''} sai da estrutura ativa. O histórico e as ligações com pessoas ficam guardados, e você pode restaurá-la depois.`}
        confirmLabel="Arquivar área"
        destructive
        busy={toggleArchive.isPending}
        onConfirm={() => archiving && toggleArchive.mutate(archiving)}
      />
    </Section>
  );
}

const UNIT_PAGE_SIZE = 20;

function UnitsSection({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [archived, setArchived] = useState(false);
  const [editing, setEditing] = useState<BusinessUnit | 'new' | null>(null);
  const [archivingUnit, setArchivingUnit] = useState<BusinessUnit | null>(null);
  const [busy, setBusy] = useState(false);
  const searchId = useId();

  useEffect(() => {
    const next = search.trim();
    if (next === q) return;
    const timer = setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, q]);

  const query = useQuery({
    queryKey: ['business-units', organizationId, { q, page, archived }],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/business-units`, {
        query: { q: q || undefined, page, pageSize: UNIT_PAGE_SIZE, archived: archived ? 'true' : undefined },
        schema: businessUnitListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['business-units', organizationId] });

  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (u: BusinessUnit) =>
      client.request(
        `/organizations/${organizationId}/business-units/${u.id}/${u.archivedAt ? 'restore' : 'archive'}`,
        {
          method: 'POST',
          schema: businessUnitSchema,
        },
      ),
    onSuccess: async (u) => {
      setArchivingUnit(null);
      toast.success(u.archivedAt ? `Unidade ${u.name} arquivada` : `Unidade ${u.name} restaurada`);
      await refresh();
    },
    onError: (err) => {
      setArchivingUnit(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });

  async function save(input: BusinessUnitInput) {
    const saved =
      editing === 'new'
        ? await client.request(`/organizations/${organizationId}/business-units`, {
            method: 'POST',
            body: input,
            schema: businessUnitSchema,
          })
        : await client.request(
            `/organizations/${organizationId}/business-units/${(editing as BusinessUnit).id}`,
            {
              method: 'PATCH',
              body: input,
              schema: businessUnitSchema,
            },
          );
    toast.success(editing === 'new' ? `Unidade ${saved.name} criada` : 'Unidade salva');
    setEditing(null);
    // O peso muda a consolidação (DEC-037): o comparativo por unidade é lido de novo.
    await Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: ['by-unit'] })]);
  }

  const data = query.data;
  useEffect(() => {
    if (data && data.totalPages > 0 && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);
  return (
    <Section
      title="Unidades de negócio"
      action={
        can('administracao', 'create') ? (
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus aria-hidden />
            Nova unidade
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-3">
        <div className="relative">
          <label htmlFor={searchId} className="sr-only">
            Buscar unidade
          </label>
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id={searchId}
            type="search"
            placeholder="Buscar por nome ou código"
            value={search}
            maxLength={100}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 accent-(--brand)"
            checked={archived}
            onChange={(e) => {
              setArchived(e.target.checked);
              setPage(1);
            }}
          />
          Ver só as arquivadas
        </label>
      </div>
      <div className="mt-4">
        {query.isPending ? (
          <LoadingState rows={3} label="Carregando unidades" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
        ) : data && data.total === 0 ? (
          <EmptyState
            className="py-8"
            icon={Store}
            title={
              q
                ? 'Nenhuma unidade encontrada'
                : archived
                  ? 'Nenhuma unidade arquivada'
                  : 'Nenhuma unidade cadastrada'
            }
            description={
              q
                ? `Nenhuma unidade corresponde a "${q}".`
                : 'Unidades de negócio (filiais, lojas, plantas) servem para filtrar indicadores e restringir o acesso de quem trabalha nelas.'
            }
          />
        ) : data ? (
          <>
            <ul
              aria-label="Unidades"
              aria-busy={query.isFetching}
              className="flex flex-col divide-y divide-border"
            >
              {data.items.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">
                      {u.name}
                      {u.code ? (
                        <span className="ml-2 font-mono text-xs text-muted-foreground">{u.code}</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {u.memberCount === 1 ? '1 pessoa' : `${u.memberCount} pessoas`}
                      {u.weight !== 1 ? ` · peso ${u.weight.toLocaleString('pt-BR')}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {!u.archivedAt && can('administracao', 'edit') ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Editar ${u.name}`}
                        onClick={() => setEditing(u)}
                      >
                        <Pencil aria-hidden />
                      </Button>
                    ) : null}
                    {can('administracao', 'delete') ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`${u.archivedAt ? 'Restaurar' : 'Arquivar'} ${u.name}`}
                        disabled={toggleArchive.isPending}
                        onClick={() => (u.archivedAt ? toggleArchive.mutate(u) : setArchivingUnit(u))}
                      >
                        {u.archivedAt ? <ArchiveRestore aria-hidden /> : <Archive aria-hidden />}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
            {data.totalPages > 1 ? (
              <nav
                aria-label="Paginação de unidades"
                className="mt-3 flex items-center justify-between text-sm"
              >
                <span className="text-muted-foreground">
                  Página {data.page} de {data.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </nav>
            ) : null}
          </>
        ) : null}
      </div>
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? 'Nova unidade' : 'Editar unidade'}
      >
        {editing !== null ? (
          <UnitForm
            initial={editing === 'new' ? undefined : editing}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archivingUnit !== null}
        onOpenChange={(open) => !open && setArchivingUnit(null)}
        title="Arquivar a unidade?"
        description={`${archivingUnit?.name ?? ''} sai das listas ativas. O histórico e as ligações com pessoas ficam guardados, e você pode restaurá-la depois.`}
        confirmLabel="Arquivar unidade"
        destructive
        busy={toggleArchive.isPending}
        onConfirm={() => archivingUnit && toggleArchive.mutate(archivingUnit)}
      />
    </Section>
  );
}

function UnitForm({
  initial,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  initial?: BusinessUnit;
  onSubmit: (input: BusinessUnitInput) => Promise<unknown>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [weight, setWeight] = useState(String(initial?.weight ?? 1).replace('.', ','));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { name: useId(), code: useId(), weight: useId() };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = businessUnitInputSchema.safeParse({ name, code, weight: weight.trim().replace(',', '.') });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome da unidade" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={120}
          placeholder="Ex.: Filial Norte"
          onChange={(e) => {
            setName(e.target.value);
            setErrors((x) => ({ ...x, name: undefined }));
          }}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.code} label="Código (opcional)" error={errors.code}>
        <Input
          {...fieldA11y(ids.code, errors.code)}
          value={code}
          maxLength={20}
          placeholder="Ex.: FIL-N"
          autoCapitalize="characters"
          onChange={(e) => {
            setCode(e.target.value);
            setErrors((x) => ({ ...x, code: undefined }));
          }}
          className="h-11 font-mono uppercase"
        />
      </FormField>
      <FormField
        id={ids.weight}
        label="Peso na consolidação"
        error={errors.weight}
        hint={
          <p className="text-xs text-muted-foreground">
            Conta nos indicadores com média ponderada pelas unidades (ex.: 3 pesa três vezes mais que 1).
          </p>
        }
      >
        <Input
          {...fieldA11y(ids.weight, errors.weight, true)}
          value={weight}
          inputMode="decimal"
          maxLength={10}
          onChange={(e) => {
            setWeight(e.target.value);
            setErrors((x) => ({ ...x, weight: undefined }));
          }}
          className="h-11 w-28"
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar unidade' : 'Criar unidade'}
        </Button>
      </div>
    </form>
  );
}
